import { Box, Flex, Icon, Image, Text, chakra } from '@chakra-ui/react';
import { FiX } from '@react-icons/all-files/fi/FiX';
import { IoPlay } from '@react-icons/all-files/io5/IoPlay';
import { useId } from 'react';

import useMarketplaceExplainerVideo from '../../hooks/useMarketplaceExplainerVideo';
import { ExplainerVideoContext } from '../../services/analytics/marketplaceEvents';
import Button from '../ui/buttons/Button';

const POSTER_SRC = '/leaseend-com-images/marketplace/explainer-v1.jpg';
const FOCUS_STYLES = {
  outline: '2px solid',
  outlineColor: 'royalBlue',
  outlineOffset: '-3px',
  boxShadow: 'none',
};

const MarketplaceExplainerVideo = (context: ExplainerVideoContext) => {
  const descriptionId = useId();
  const video = useMarketplaceExplainerVideo(context);

  return (
    <Box ref={video.containerRef} w="full" maxW="600px" mx="auto" my={4}>
      <Box
        position="relative"
        bg={video.view === 'card' ? 'iceBlue' : 'white'}
        border="1px solid"
        borderColor={video.view === 'card' ? 'mistBlue' : 'grayMediumBackground'}
        borderRadius="12px"
        overflow="hidden"
      >
        {video.view === 'collapsed' && (
          <Button
            variant="unstyled"
            type="button"
            display="flex"
            w="full"
            h="auto"
            minH="44px"
            px={3}
            py={2}
            gap={2}
            whiteSpace="normal"
            fontSize={{ base: '12px', sm: '13px', md: '14px' }}
            color="leaseEndBlue"
            _hover={{ bg: 'ivoryWarm' }}
            _focusVisible={FOCUS_STYLES}
            onClick={() => video.setCollapsed(false)}
          >
            <Flex
              as="span"
              boxSize={5}
              align="center"
              justify="center"
              bg="leaseEndYellow"
              borderRadius="full"
              flexShrink={0}
            >
              <Icon as={IoPlay} boxSize={3} color="grayDark" aria-hidden />
            </Flex>
            Watch how selling your lease works
          </Button>
        )}
        {video.view === 'card' && (
          <Button
            variant="unstyled"
            type="button"
            display="flex"
            w="full"
            h="auto"
            py={{ base: 3, md: '14px' }}
            pl={{ base: 3, md: 4 }}
            pr={9}
            gap={{ base: '14px', md: '18px' }}
            whiteSpace="normal"
            textAlign="left"
            color="grayDark"
            _hover={{ bg: 'oceanBoatBlueBG' }}
            _focusVisible={FOCUS_STYLES}
            aria-label="Play how selling your lease works"
            aria-describedby={descriptionId}
            onClick={video.play}
          >
            <Box
              as="span"
              position="relative"
              w={{ base: '112px', sm: '128px', md: '176px' }}
              flexShrink={0}
            >
              <Image
                src={POSTER_SRC}
                alt=""
                aspectRatio={16 / 9}
                w="full"
                borderRadius="8px"
                border="1px solid"
                borderColor="grayMediumBackground"
              />
              <Flex
                as="span"
                position="absolute"
                bottom="10px"
                left="10px"
                boxSize="30px"
                align="center"
                justify="center"
                bg="leaseEndYellow"
                borderRadius="full"
              >
                <Icon as={IoPlay} boxSize={4} color="grayDark" aria-hidden />
              </Flex>
            </Box>
            <Box as="span" flex={1} minW={0}>
              <Text
                as="span"
                display="block"
                fontSize={{ base: '15px', md: '17px' }}
                fontWeight="800"
                lineHeight="1.25"
                color="leaseEndBlue"
              >
                How selling your lease works
              </Text>
              <Text
                id={descriptionId}
                as="span"
                display="block"
                mt={1}
                fontSize={{ base: '12.5px', md: '13.5px' }}
                lineHeight="1.4"
                fontWeight="400"
                color="grayLight"
              >
                No fee to list, no fee to sell. The buyer pays us.
              </Text>
            </Box>
          </Button>
        )}
        <chakra.video
          ref={video.videoRef}
          display={video.view === 'player' ? 'block' : 'none'}
          w="full"
          aspectRatio={16 / 9}
          bg="black"
          controls
          playsInline
          preload="none"
          poster={video.view === 'player' ? POSTER_SRC : undefined}
          aria-label="How selling your lease works"
          tabIndex={video.view === 'player' ? 0 : -1}
          onPlaying={video.onPlaying}
          onTimeUpdate={video.onTimeUpdate}
          onEnded={video.onEnded}
          onError={video.onError}
        />
        {video.view !== 'collapsed' && (
          <Button
            variant="unstyled"
            type="button"
            position="absolute"
            top={0}
            right={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxSize="36px"
            minW="36px"
            p={0}
            bg={video.view === 'player' ? 'blackAlpha.700' : 'transparent'}
            color={video.view === 'player' ? 'white' : 'grayLight'}
            borderRadius="full"
            _focusVisible={{
              ...FOCUS_STYLES,
              outlineColor: video.view === 'player' ? 'leaseEndYellow' : 'royalBlue',
            }}
            aria-label={video.view === 'player' ? 'Close video' : 'Collapse video card'}
            onClick={video.view === 'player' ? video.close : () => video.setCollapsed(true)}
          >
            <Icon as={FiX} boxSize={4} aria-hidden />
          </Button>
        )}
      </Box>
      {video.view === 'player' && video.hasError && (
        <Text role="alert" fontSize="sm" mt={2} color="grayLight">
          The video couldn't load. You can keep filling out the form.
        </Text>
      )}
    </Box>
  );
};

export default MarketplaceExplainerVideo;
